/**
 * materializeDraftStorage — persistance brouillon du wizard materialize enrichi
 * en sessionStorage (Chunk 9.T.5.v3.6).
 *
 * Parity design `intakeDraftStorage.ts` (`_intake_mvp`) mais scellé par
 * `intake_event_id` pour permettre plusieurs onglets sur plusieurs events
 * distincts sans collision (QA §4 v3.6 PO 2026-07-14).
 *
 * Cleanup rules (QA §4) :
 *   - Après succès de la mutation `useMaterializeIntakeEvent`
 *   - Sur rejet du wizard (bouton Annuler / fermeture dialog)
 *   - Sur changement d'event (nouveau `intake_event_id` dans URL, `useEffect`
 *     du wizard cleanup l'ancien draft)
 *   - Si draft schema mismatch au load (validation Zod échoue → silent remove)
 *
 * Contenu stocké (QA §4 "pas de données sensibles inutiles") :
 *   - `intake_event_id` (contexte)
 *   - `currentStep` (progression 1..6)
 *   - `strategy` + `existing_project_id` / `existing_company_id` (UUIDs)
 *   - `new_company` / `new_project` (champs form saisis broker — no PII sensible)
 *   - `confirmedCreate` (bool)
 *   - `docDirection` (enum 4 valeurs)
 *   - `applyProjectData` (canoniques Q7.a validés par broker)
 *   - `applyCompanyData` (employee_count)
 *   - `createQuoteRequestEnabled` (bool toggle)
 *   - `createQuoteRequest` (target_insurers + title + expected_response_by)
 *
 * NON stocké :
 *   - Aucune donnée session/JWT/API key
 *   - Aucune valeur brute extracted_fields (elles restent en DB via v3.3)
 *   - Aucun payload event / classification (relu à chaque mount depuis
 *     `useIntakeEvent`)
 */

import { z } from "zod";

const STORAGE_KEY_PREFIX = "kollectivia.intake.materialize.draft.v1.";
const SCHEMA_VERSION = 1;

// -----------------------------------------------------------------------------
// Schéma du draft — validé Zod à la lecture ET à l'écriture (parity intakeDraftStorage)
// -----------------------------------------------------------------------------

const StrategySchema = z.enum([
  "attach_existing_project",
  "attach_company_create_project",
  "create_company_and_project",
]);

const DocDirectionSchema = z.enum([
  "insurer_response",
  "client_input",
  "advisor_document",
  "unknown",
]);

/**
 * Shape draft — mirror partiel du `MaterializeIntakeEventSchema` v3.5 côté shared.
 * Tous les champs sont optionnels (draft à n'importe quelle étape).
 */
const MaterializeDraftDataSchema = z.object({
  // Step 1 — doc_direction
  docDirection: DocDirectionSchema.optional(),

  // Step 2 — strategy + company
  strategy: StrategySchema.optional(),
  existingCompanyId: z.string().uuid().optional(),
  newCompany: z
    .object({
      legal_name: z.string().max(200).optional(),
      language: z.enum(["fr", "en"]).optional(),
      identification_type: z.string().max(50).optional(),
      business_number: z.string().max(50).optional(),
      postal_code: z.string().max(20).optional(),
    })
    .optional(),

  // Step 3 — project
  existingProjectId: z.string().uuid().optional(),
  newProject: z
    .object({
      title: z.string().max(200).optional(),
      insurer_codes: z.array(z.string()).max(10).optional(),
      guarantee_codes: z.array(z.string()).max(30).optional(),
      effect_date: z.string().optional(),
      renewal_target_date: z.string().optional(),
      deadline_date: z.string().optional(),
    })
    .optional(),
  confirmedCreate: z.boolean().optional(),

  // Step 4 — apply canoniques Q7.a (broker-validated)
  applyProjectData: z
    .object({
      insurer_codes: z.array(z.string()).max(10).optional(),
      guarantee_codes: z.array(z.string()).max(30).optional(),
      effect_date: z.string().optional(),
      renewal_target_date: z.string().optional(),
      deadline_date: z.string().optional(),
    })
    .optional(),
  applyCompanyData: z
    .object({
      employee_count: z.number().int().min(0).max(1_000_000).optional(),
    })
    .optional(),

  // Step 5 — create_quote_request toggle + config
  createQuoteRequestEnabled: z.boolean().optional(),
  createQuoteRequest: z
    .object({
      target_insurers: z.array(z.string()).max(10).optional(),
      title: z.string().max(200).optional(),
      expected_response_by: z.string().optional(),
    })
    .optional(),
});
export type MaterializeDraftData = z.infer<typeof MaterializeDraftDataSchema>;

const DraftEnvelopeSchema = z.object({
  kind: z.literal("materialize"),
  version: z.literal(SCHEMA_VERSION),
  intakeEventId: z.string().uuid(),
  currentStep: z.number().int().min(1).max(6),
  updatedAt: z.string(),
  data: MaterializeDraftDataSchema,
});
export type MaterializeDraft = z.infer<typeof DraftEnvelopeSchema>;

// -----------------------------------------------------------------------------
// Safe accès sessionStorage
// -----------------------------------------------------------------------------

function safeGetItem(key: string): string | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetItem(key: string, value: string): boolean {
  try {
    if (typeof window === "undefined") return false;
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
function safeRemoveItem(key: string): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(key);
  } catch {
    /* swallow */
  }
}

function keyForEvent(intakeEventId: string): string {
  return `${STORAGE_KEY_PREFIX}${intakeEventId}`;
}

// -----------------------------------------------------------------------------
// API publique
// -----------------------------------------------------------------------------

export function loadMaterializeDraft(intakeEventId: string): MaterializeDraft | null {
  const raw = safeGetItem(keyForEvent(intakeEventId));
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    safeRemoveItem(keyForEvent(intakeEventId));
    return null;
  }

  const result = DraftEnvelopeSchema.safeParse(parsed);
  if (!result.success) {
    // Schema mismatch → silent cleanup + fresh start (QA §4).
    safeRemoveItem(keyForEvent(intakeEventId));
    return null;
  }

  // Guard : le draft doit correspondre à l'event demandé (parity intakeDraftStorage
  // "kind mismatch" — sanity check anti-collision).
  if (result.data.intakeEventId !== intakeEventId) {
    return null;
  }

  return result.data;
}

export function saveMaterializeDraft(
  intakeEventId: string,
  currentStep: 1 | 2 | 3 | 4 | 5 | 6,
  data: MaterializeDraftData,
): boolean {
  const envelope: MaterializeDraft = {
    kind: "materialize",
    version: SCHEMA_VERSION,
    intakeEventId,
    currentStep,
    updatedAt: new Date().toISOString(),
    data,
  };
  const valid = DraftEnvelopeSchema.safeParse(envelope);
  if (!valid.success) return false;

  return safeSetItem(keyForEvent(intakeEventId), JSON.stringify(envelope));
}

export function clearMaterializeDraft(intakeEventId: string): void {
  safeRemoveItem(keyForEvent(intakeEventId));
}

/**
 * Détecte si le draft est non-vide — utilisé par ResumeDraftScreen pour
 * décider s'il faut afficher l'écran de reprise ou pas.
 */
export function isMaterializeDraftDirty(data: MaterializeDraftData | undefined): boolean {
  if (!data) return false;
  return (
    !!data.docDirection ||
    !!data.strategy ||
    !!data.existingCompanyId ||
    !!data.existingProjectId ||
    !!data.newCompany?.legal_name?.trim() ||
    !!data.newProject?.title?.trim() ||
    (data.newProject?.insurer_codes?.length ?? 0) > 0 ||
    !!data.applyProjectData ||
    !!data.applyCompanyData?.employee_count ||
    !!data.createQuoteRequestEnabled
  );
}
